import React, {ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {Text} from '../Text'

interface Props extends React.HTMLProps<HTMLInputElement> {
  /** The boolean value of the component that represents on/off, checked/unchecked */
  checked: boolean
  /** A label for the toggle */
  children?: ReactNode
  /** Pass through classname to allow styles overrides */
  className?: string
  /** The id for label and input element */
  id: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** The toggle click handler */
  onChange: (event: React.SyntheticEvent<Element, Event>) => void
  /** Use larger touch area */
  hasTouchArea?: boolean
  /** Optional aria-labelledby for screen-readers, used when labelled by another element */
  ariaLabelledBy?: string
  /** Whether the toggle is disabled or not */
  disabled?: boolean
}

const TRACK_MARGIN = 4
const THUMB_SIZE = 16
const TOUCHAREA_SIZE_LG = 10

const Switch = styled.div`
  position: relative;
  display: inline-flex;
  width: ${cssTheme.layout.spacing.s800};
  height: ${cssTheme.layout.spacing.s600};
  .isDisabled & {
    opacity: 0.5;
  }
`

const Slider = styled.div`
  position: absolute;
  border-radius: ${cssTheme.layout.radius.rounded};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${cssTheme.colors.toggle.default.background};
  transition: background-color 0.2s ease-in-out;

  &:before {
    content: '';
    position: absolute;
    border-radius: ${cssTheme.layout.radius.rounded};
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    top: ${TRACK_MARGIN}px;
    left: ${TRACK_MARGIN}px;
    background-color: ${cssTheme.colors.toggle.default.thumb};
    transition: transform 0.2s ease-in-out;
  }

  &:after {
    content: '';
    position: absolute;
    left: -2px;
    right: -2px;
    top: -2px;
    bottom: -2px;
    box-shadow: 0 0 0 1px ${cssTheme.colors.toggle.default.focusRing};
    border-radius: ${cssTheme.layout.radius.rounded};
    opacity: 0;
    visibility: hidden;
  }
`

const Input = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
  left: -20px;
  top: 0;

  &:checked + ${Slider} {
    background-color: ${cssTheme.colors.toggle.checked.background};
    &:before {
      transform: translateX(16px);
      background-color: ${cssTheme.colors.toggle.checked.thumb};
    }
    &:after {
      box-shadow: 0 0 0 1px ${cssTheme.colors.toggle.checked.focusRing};
    }
  }

  &:focus-visible + ${Slider} {
    &:after {
      opacity: 1;
      visibility: visible;
    }
  }
`

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  gap: ${cssTheme.layout.spacing.s300};
  position: relative;
  color: ${cssTheme.colors.toggle.default.label};
  &.hasTouchArea {
    padding: ${TOUCHAREA_SIZE_LG}px 0;
  }
  &.isChecked {
    color: ${cssTheme.colors.toggle.checked.label};
  }
  &.isDisabled {
    cursor: not-allowed;
    color: ${cssTheme.colors.toggle.disabled.label};
  }
`

export const Toggle = ({
  onChange,
  checked,
  children,
  id,
  dataId,
  hasTouchArea,
  className,
  ariaLabelledBy,
  disabled
}: Props) => {
  return (
    <Label
      htmlFor={id}
      data-id={dataId}
      className={cx(
        hasTouchArea && 'hasTouchArea',
        checked && 'isChecked',
        disabled && 'isDisabled',
        className
      )}
    >
      <Switch>
        <Input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          aria-labelledby={ariaLabelledBy}
          disabled={disabled}
        />
        <Slider />
      </Switch>
      {children && <Text variant="bodyS">{children}</Text>}
    </Label>
  )
}
